import * as React from 'react';
import ProjectLayout from '../ProjectLayout';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import SurveyHeader from '../survey/SurveyHeader';
import { css } from '@emotion/core';
import { COLORS } from '../../globalStyle/colors';
import { onMobileDevice } from '../../globalStyle/mediaQueries';
import { useAppState } from '../../core/redux/hooks';
import * as R from 'ramda';
import { useMount } from '../../core/hooks/useMount';
import { useDispatch } from 'react-redux';
import { getECourseUrlAction } from '../api/getECourseUrlAction';
import { getECoursePopupAction } from '../api/getECoursePopupAction';
import { getECourseDisplayNameAction } from '../api/getECourseDisplayNameAction';
import { useProjIds } from '../../navigation/hooks';
import { Loader } from '../../loading/Loader';
import { useError } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import Msg from '../../localization/Msg';


const styles = {
  content:       css`
    margin-top: 62px;
    background-color: ${COLORS.WHITE};
    width: 100%;

    ${onMobileDevice} {
      margin-top: 20px;
    }
  `,
  header: css`
    ${onMobileDevice} {
      margin-top: 37px;
    }  
  `,
  loader:        css`
    width: 150px;
    height: 150px;
    margin: auto;
  `,
  iframe:        css`
    width: 100%;
    height: calc(100vh - 201px);

    ${onMobileDevice} {
      height: calc(100vh - 53px);
    }
  `,  
  iframeHidden:  css`
    width: 0;
    height: 0;
  `,
  iframeLoading: css`
    width: 100%;
    height: calc(100vh - 201px);
    opacity: 0;
    position: absolute;

    ${onMobileDevice} {
      height: calc(100vh - 53px);
    }
  `,
  loaderOverlay: css`
    width: 150px;
    height: 150px;
    margin: auto;
  `,
  iframeContainer: css`
    position: relative;
    width: 100%;
    margin-top: 30px;
    height: calc(100vh - 201px);

    ${onMobileDevice} {
      height: calc(100vh - 53px);
    }
  `,
  eCourseMessage: css`
    margin-top: 37px;
    display: block;
  `,
  
  link:           css`
    display: block;
    padding: 18px 15px;
    text-decoration: none;
    
    &:hover {
      cursor: pointer;
    }
  `,
};


type Props = {
  className?: string;
};


/**
 * ECourse: a component that displays an eLearning course
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ECourse: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const projIds       = useProjIds();
  const participantId = projIds.participantId;
  const projectId     = projIds.projId;
  const url           = useAppState(state => state.eCourseUrl);
  const popup         = useAppState(state => state.eCoursePopup);
  const displayName   = useAppState(state => state.eCourseDisplayName);
  const defaultTitle  = useMsgText(Str.ECourse);

  const pageTitle = displayName?.trim() || defaultTitle;

  const useUrl = React.useMemo(() => url, [url]);
  const usePopup = React.useMemo(() => popup, [popup]);

  const error  = useError(ActionType.GetECourseUrlRequest, `${projectId}`);

  const preEnrollStatus = useAppState(state => state.eCourse?.preEnrollStatus);

  const [iframeLoaded, setIframeLoaded] = React.useState(false);

  const handleIframeLoad = React.useCallback(() => {
    setIframeLoaded(true);
  }, []);

  // Reset loaded state if URL changes (e.g. new course)
  React.useEffect(() => {
    setIframeLoaded(false);
  }, [url]);

  // Track whether we used the preEnroll fast-path so we can retry without it on failure.
  const triedWithOverridesRef = React.useRef(false);
  const retriedRef            = React.useRef(false);

  useMount(() => {
    // always get new URL since they are only valid for 2 minutes
    const useCached = preEnrollStatus?.status === 'success'
                      && preEnrollStatus.projectId === projectId
                      && preEnrollStatus.participantId === participantId
                      && !!preEnrollStatus.data;

    if (useCached) {
      triedWithOverridesRef.current = true;
      dispatch(getECourseUrlAction(projectId, participantId, {
        registered:   true,
        courseIdStr:  preEnrollStatus!.data!.courseId,
        moodleUserId: preEnrollStatus!.data!.moodleUserId,
      }));
    } else {
      dispatch(getECourseUrlAction(projectId, participantId));
    }

    dispatch(getECoursePopupAction(projectId));
    dispatch(getECourseDisplayNameAction(projectId));

    return () => {
      dispatch({ type: ActionType.GetECourseUrlResponse, payload: null, meta: { projectId } });
    };
  });

  // One-time retry: if the optimistic call (with Registered/CourseIdStr/MoodleUserId) failed,
  // fall back to the standard flow that re-runs IsEnrolled on the server.
  React.useEffect(
    () => {
      if (
        !!error
        && triedWithOverridesRef.current
        && !retriedRef.current
      ) {
        retriedRef.current = true;
        dispatch(getECourseUrlAction(projectId, participantId));
      }
    },
    [error, dispatch, projectId, participantId],
  );

  return (
    <ProjectLayout className={props.className}>
      <SurveyHeader title={`/ ${pageTitle}`} deadline={undefined} css={styles.header}/>

      {
        R.isNil(useUrl)
          ? !!error
            ? <Msg defaultMsg='X' str={Str.ECourseRegistrationFailed} css={styles.eCourseMessage} />
            : <Loader css={styles.loader} />
          : usePopup
            ? <>
                <Msg defaultMsg='X' str={Str.ECoursePopupMsg} css={styles.eCourseMessage} />
                <iframe
                  frameBorder={0}
                  css={styles.iframeHidden}
                  src={url}
                  title='e-course-hidden'
                />
              </>
            : <>
                <div css={styles.iframeContainer}>
                  {!iframeLoaded && <Loader css={styles.loaderOverlay} />}
                  <iframe
                    frameBorder={0}
                    css={iframeLoaded ? styles.iframe : styles.iframeLoading}
                    src={url}
                    title='e-course'
                    onLoad={handleIframeLoad}
                  />
                </div>
              </>
      }
    </ProjectLayout>
  )

};

export default ECourse;
